import { useState, useEffect, useCallback, useRef } from 'react';
import LogoMain from '../LogoMain.png';
import LyricLogo from '../LyricLogo.png';

// Full-screen, contained viewer for a deployed app. Instead of popping the app
// out into a new browser tab, we render it in an <iframe> inside Lyric with a
// header bar (status, reload, open-in-new-tab fallback, close). Closing the
// overlay unmounts the iframe, fully tearing down the embedded app.
//
// Deployed apps are served same-origin under /apps/<name>/ (see getDeployUrl),
// and Lyric injects the Okta token into the served HTML regardless of tab vs
// frame, so the embedded app behaves exactly like the new-tab version. This is
// the same embedding the Visual Editor already relies on, minus the edit layer.
//
// props:
//   app      — { appName, displayName, url, status }
//   onClose  — called when the user closes the viewer
const MIN_LOADING_MS = 1000;
const CLOSING_NOTICE_MS = 500;

function AppViewerOverlay({ app, onClose }) {
    // Bumping the key remounts the iframe → a clean reload of the embedded app.
    const [reloadKey, setReloadKey] = useState(0);
    // Gates the loading screen: cleared once the iframe fires its first load.
    const [loaded, setLoaded] = useState(false);
    // Keep the branded loading screen up for at least MIN_LOADING_MS so opening
    // an app always feels deliberate (and covers the brief same-origin flash).
    const [minElapsed, setMinElapsed] = useState(false);
    // Close flow: null (open) → 'confirm' (are-you-sure) → 'closing' (brief
    // "Closing…" notice) → onClose(). Escape/Close button start the flow.
    const [closing, setClosing] = useState(null);

    const overlayRef = useRef(null);
    const iframeRef = useRef(null);
    const closeBtnRef = useRef(null);
    const scrimRef = useRef(null);
    // Mirror `closing` into a ref so the window/iframe keydown listeners (bound
    // once) can read the current phase without being re-bound.
    const closingRef = useRef(closing);
    closingRef.current = closing;
    // Always call the latest onClose (avoids stale closures in the iframe's own
    // Escape listener, which is attached once per load).
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    const name = app?.displayName || app?.appName || 'App';
    const url = app?.url;
    const isIdle = app?.status === 'idle';
    const showLoading = !loaded || !minElapsed;

    // Escape / Close toggles the confirm step; while confirming, Escape cancels;
    // while already closing, it's a no-op.
    const requestClose = useCallback(() => {
        const phase = closingRef.current;
        if (phase === 'closing') return;
        setClosing(phase === 'confirm' ? null : 'confirm');
    }, []);
    const cancelClose = useCallback(() => setClosing(null), []);
    const confirmClose = useCallback(() => setClosing('closing'), []);

    const handleReload = useCallback(() => {
        setLoaded(false);
        setReloadKey((k) => k + 1);
    }, []);

    // When the iframe loads, reveal it — and, for same-origin apps, also listen
    // for Escape *inside* the iframe so the close flow starts even when focus is
    // in the embedded app. Cross-origin (dev) access throws → silently skipped.
    const handleIframeLoad = useCallback(() => {
        setLoaded(true);
        try {
            const doc = iframeRef.current?.contentDocument;
            if (doc) {
                doc.addEventListener('keydown', (e) => {
                    if (e.key === 'Escape') requestClose();
                });
            }
        } catch { /* cross-origin iframe — cannot attach */ }
    }, [requestClose]);

    // Once in the 'closing' phase, show the brief notice then actually close.
    useEffect(() => {
        if (closing !== 'closing') return undefined;
        const t = setTimeout(() => onCloseRef.current?.(), CLOSING_NOTICE_MS);
        return () => clearTimeout(t);
    }, [closing]);

    // Dismissing the confirm step (Cancel button or Escape) removes the focused
    // danger button, which would leave focus on <body>. Return focus to the
    // Close button so keyboard/screen-reader users keep their position while the
    // viewer stays open.
    const prevClosingRef = useRef(closing);
    useEffect(() => {
        const prev = prevClosingRef.current;
        prevClosingRef.current = closing;
        if (prev === 'confirm' && closing === null && closeBtnRef.current) {
            closeBtnRef.current.focus();
        }
    }, [closing]);

    // Restart the minimum-display timer on first mount and on every reload.
    useEffect(() => {
        setMinElapsed(false);
        const t = setTimeout(() => setMinElapsed(true), MIN_LOADING_MS);
        return () => clearTimeout(t);
    }, [reloadKey]);

    // Modal behaviour: Escape-to-close, focus trap, focus restore, scroll lock.
    useEffect(() => {
        const overlay = overlayRef.current;
        const prevFocused = document.activeElement;

        const focusableInOverlay = () => {
            // While the confirm step is up, trap within its card so Tab can't
            // reach the controls behind the scrim.
            const root = (closingRef.current === 'confirm' && scrimRef.current) ? scrimRef.current : overlay;
            if (!root) return [];
            const sel = 'button:not([disabled]), a[href], iframe, [tabindex]:not([tabindex="-1"])';
            return Array.from(root.querySelectorAll(sel)).filter((el) => el.offsetParent !== null);
        };

        const onKey = (e) => {
            if (e.key === 'Escape') { e.preventDefault(); requestClose(); return; }
            if (e.key !== 'Tab') return;
            // Keep Tab focus within the active surface so it can't fall back to
            // the dashboard behind the overlay.
            const items = focusableInOverlay();
            if (items.length === 0) return;
            const first = items[0];
            const last = items[items.length - 1];
            const active = document.activeElement;
            const root = (closingRef.current === 'confirm' && scrimRef.current) ? scrimRef.current : overlay;
            if (e.shiftKey) {
                if (active === first || !root.contains(active)) { e.preventDefault(); last.focus(); }
            } else if (active === last || !root.contains(active)) {
                e.preventDefault();
                first.focus();
            }
        };

        window.addEventListener('keydown', onKey);
        // Move focus into the dialog on open.
        if (closeBtnRef.current) closeBtnRef.current.focus();

        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
            // Restore focus to whatever was focused before the viewer opened.
            if (prevFocused && typeof prevFocused.focus === 'function') {
                try { prevFocused.focus(); } catch { /* element gone */ }
            }
        };
    }, [requestClose]);

    if (!app) return null;

    return (
        <div ref={overlayRef} style={styles.overlay} role="dialog" aria-modal="true" aria-label={`${name} (embedded)`}>
            <div style={styles.header}>
                <img src={LogoMain} alt="RapidAI" style={styles.headerLogo} />
                <span style={styles.title} title={name}>{name}</span>
                <span style={styles.statusDot(!loaded)} />
                <span style={styles.statusLabel}>{loaded ? 'Live' : (isIdle ? 'Waking…' : 'Starting…')}</span>

                <div style={{ flex: 1 }} />

                <button type="button" onClick={handleReload} style={styles.barBtn} title="Reload the app">↻ Reload</button>
                {url && (
                    <a href={url} target="_blank" rel="noopener noreferrer" style={{ ...styles.barBtn, textDecoration: 'none' }} title="Open in a new browser tab">
                        Open in new tab ↗
                    </a>
                )}
                <button ref={closeBtnRef} type="button" onClick={requestClose} style={{ ...styles.barBtn, ...styles.closeBtn }} title="Close (Esc)">✕ Close</button>
            </div>

            <div style={styles.body}>
                {url ? (
                    <iframe
                        ref={iframeRef}
                        key={reloadKey}
                        title={`${name} (embedded)`}
                        src={url}
                        onLoad={handleIframeLoad}
                        allow="clipboard-read; clipboard-write; fullscreen; geolocation; microphone; camera"
                        style={styles.iframe}
                    />
                ) : null}
                {(showLoading || !url) && (
                    <div style={styles.loading} aria-live="polite">
                        <div style={styles.brandRow}>
                            <img src={LyricLogo} alt="" style={styles.loadingLogo} />
                            <span style={styles.lyricWordmark}>Lyric</span>
                        </div>
                        {url ? (
                            <>
                                <div style={styles.loadingTitle}>Opening {name}</div>
                                <div style={styles.loadingSub}>
                                    {isIdle ? 'Waking the app — this can take a few seconds…' : 'Starting the app in a secure window…'}
                                </div>
                                <div style={styles.progressTrack}>
                                    <div key={reloadKey} style={styles.progressFill} />
                                </div>
                                <div style={styles.poweredBy}>
                                    <span>Powered by</span>
                                    <img src={LogoMain} alt="RapidAI" style={styles.poweredByLogo} />
                                </div>
                            </>
                        ) : (
                            <div style={{ color: '#ef9a9a', fontSize: 14, marginTop: 12 }}>This app has no URL to open.</div>
                        )}
                    </div>
                )}
            </div>

            {closing === 'confirm' && (
                <div ref={scrimRef} style={styles.scrim} role="alertdialog" aria-modal="true" aria-label={`Close ${name}?`}>
                    <div style={styles.confirmCard}>
                        <div style={styles.confirmTitle}>Close {name}?</div>
                        <div style={styles.confirmMsg}>
                            This closes the app window. The app keeps running — you can reopen it anytime from the dashboard.
                        </div>
                        <div style={styles.confirmBtnRow}>
                            <button type="button" onClick={cancelClose} style={styles.barBtn}>Cancel</button>
                            <button type="button" onClick={confirmClose} autoFocus style={{ ...styles.barBtn, ...styles.confirmDangerBtn }}>Close app</button>
                        </div>
                    </div>
                </div>
            )}

            {closing === 'closing' && (
                <div style={styles.scrim} aria-live="assertive">
                    <div style={styles.closingCard}>
                        <img src={LyricLogo} alt="" style={styles.closingLogo} />
                        <span style={styles.closingText}>Closing {name}…</span>
                    </div>
                </div>
            )}

            <style>{spinnerKeyframes}</style>
        </div>
    );
}

const spinnerKeyframes = [
    '@keyframes lyricAppViewerProgress { 0% { width: 4%; } 70% { width: 88%; } 100% { width: 96%; } }',
    '@keyframes lyricAppViewerPulse { 0%, 100% { opacity: 0.55; transform: scale(1); } 50% { opacity: 1; transform: scale(1.04); } }',
].join('\n');

const styles = {
    overlay: {
        position: 'fixed',
        inset: 0,
        zIndex: 100000,
        background: '#0d0a18',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 12px',
        background: '#17132a',
        borderBottom: '1px solid #2a2440',
    },
    headerLogo: {
        height: 22,
        width: 'auto',
        display: 'block',
        flexShrink: 0,
        opacity: 0.95,
    },
    title: {
        color: '#ede7f6',
        fontSize: 14,
        fontWeight: 600,
        maxWidth: 360,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    statusDot: (isIdle) => ({
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: isIdle ? '#9575cd' : '#4caf50',
        boxShadow: `0 0 6px ${isIdle ? '#9575cd' : '#4caf50'}`,
        marginLeft: 4,
    }),
    statusLabel: { color: '#9e9e9e', fontSize: 11, fontWeight: 600 },
    barBtn: {
        padding: '6px 12px',
        fontSize: 12,
        fontWeight: 600,
        background: '#1c1732',
        color: '#b39ddb',
        border: '1px solid #4a3f6e',
        borderRadius: 6,
        cursor: 'pointer',
        lineHeight: 1,
    },
    closeBtn: { borderColor: '#5a4a4a', color: '#ffab91' },
    scrim: {
        position: 'absolute',
        inset: 0,
        zIndex: 5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(8, 5, 18, 0.72)',
        backdropFilter: 'blur(3px)',
        WebkitBackdropFilter: 'blur(3px)',
    },
    confirmCard: {
        width: 380,
        maxWidth: '86vw',
        background: '#1c1732',
        border: '1px solid #3a2f5e',
        borderRadius: 14,
        padding: '22px 24px',
        boxShadow: '0 18px 60px rgba(0,0,0,0.55)',
        textAlign: 'center',
    },
    confirmTitle: { color: '#ede7f6', fontSize: 18, fontWeight: 700 },
    confirmMsg: { color: '#b0a4d0', fontSize: 13, lineHeight: 1.5, marginTop: 10 },
    confirmBtnRow: { display: 'flex', gap: 10, justifyContent: 'center', marginTop: 20 },
    confirmDangerBtn: {
        background: '#3a1d1d',
        color: '#ff8a80',
        borderColor: '#a3433b',
    },
    closingCard: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 22px',
        background: '#1c1732',
        border: '1px solid #3a2f5e',
        borderRadius: 999,
        boxShadow: '0 14px 44px rgba(0,0,0,0.5)',
    },
    closingLogo: {
        height: 24,
        width: 24,
        display: 'block',
        filter: 'drop-shadow(0 0 10px rgba(239,40,40,0.5))',
        animation: 'lyricAppViewerPulse 1.1s ease-in-out infinite',
    },
    closingText: { color: '#ede7f6', fontSize: 14, fontWeight: 600 },
    body: { flex: 1, minHeight: 0, position: 'relative', background: '#fff' },
    iframe: { width: '100%', height: '100%', border: 'none', display: 'block' },
    loading: {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'radial-gradient(circle at 50% 38%, #1a1533 0%, #0d0a18 70%)',
        zIndex: 2,
    },
    brandRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        marginBottom: 26,
    },
    loadingLogo: {
        height: 60,
        width: 60,
        display: 'block',
        filter: 'drop-shadow(0 0 16px rgba(239,40,40,0.5))',
        animation: 'lyricAppViewerPulse 1.6s ease-in-out infinite',
    },
    lyricWordmark: {
        color: '#f5f0ff',
        fontSize: 40,
        fontWeight: 800,
        letterSpacing: 0.5,
        lineHeight: 1,
    },
    loadingTitle: {
        color: '#ede7f6',
        fontSize: 20,
        fontWeight: 700,
        letterSpacing: 0.2,
        textAlign: 'center',
        padding: '0 24px',
    },
    loadingSub: {
        color: '#9e8fcf',
        fontSize: 13,
        marginTop: 8,
        textAlign: 'center',
        padding: '0 24px',
    },
    progressTrack: {
        marginTop: 22,
        width: 240,
        maxWidth: '70vw',
        height: 4,
        borderRadius: 4,
        background: '#2a2440',
        overflow: 'hidden',
    },
    progressFill: {
        height: '100%',
        width: '4%',
        borderRadius: 4,
        background: 'linear-gradient(90deg, #7c4dff, #4fc3f7)',
        animation: `lyricAppViewerProgress ${MIN_LOADING_MS}ms ease-out forwards`,
    },
    poweredBy: {
        position: 'absolute',
        bottom: 28,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        color: '#6b6189',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.3,
    },
    poweredByLogo: {
        height: 14,
        width: 'auto',
        display: 'block',
        opacity: 0.8,
    },
};

export default AppViewerOverlay;
